import type { ReactNode } from 'react'

import { Button } from '#/components/ui/button'
import { cn } from '#/lib/utils'
import { LuSend } from 'react-icons/lu'
import { CgMoreO } from 'react-icons/cg'
import { Link } from '@tanstack/react-router'

import { getImageUrl } from '#/lib/imageUrl'

type CourseCardProps = {
  id?: string | number
  slug?: string
  image: string
  tag?: string
  rating?: string
  ratingIcon?: ReactNode
  meta?: Array<{ icon: ReactNode; text: string }>
  title: string
  description: string
  price?: string
  priceOriginal?: string
  className?: string
}

export default function CourseCard({
  title,
  description,
  className,
  image,
  rating,
  ratingIcon,
  tag,
  meta,
  price,
  priceOriginal,
  id,
  slug,
}: CourseCardProps) {
  const courseSlug = slug || (typeof id === 'string' ? id : undefined)
  const courseId = id ?? 1
  return (
    <div
      className={cn(
        'flex w-full flex-col items-start overflow-clip rounded-[12px] bg-white shadow-[0px_4px_20px_0px_rgba(106,155,145,0.16)]',
        className,
      )}
    >
      <div className="relative h-[230px] z-10 w-full">
        <div
          className="absolute inset-0 z-[5] pointer-events-none"
          style={{
            background:
              'linear-gradient(to bottom, rgba(255,255,255,0.4) 20%, transparent 100%)',
          }}
        />
        <img
          alt=""
          className="absolute inset-0 size-full object-cover"
          loading="lazy"
          src={getImageUrl(image)}
        />
        {rating ? (
          <div className="absolute bottom-[-30px] left-[54px] flex size-[60px] items-center justify-center gap-[2px] rounded-[999px] bg-white shadow-[0px_4px_16px_0px_rgba(0,0,0,0.1)]">
            {ratingIcon ? (
              <span className="text-[16px] text-[color:var(--primary-700)]">
                {ratingIcon}
              </span>
            ) : null}
            <span className="text-[18px] font-bold text-[color:var(--neutral-600)]">
              {rating}
            </span>
          </div>
        ) : null}
        {tag && (
          <div className="absolute z-10 right-[16px] top-[16px] flex items-center rounded-full bg-secondary-500 px-[16px] py-[8px] ">
            <p className="text-[14px] font-semibold   leading-none text-white">
              {tag}
            </p>
          </div>
        )}
      </div>
      <div className="flex w-full flex-col items-start gap-[16px] px-[30px] pb-[30px] pt-[30px]">
        {meta && (
          <div className="flex flex-wrap items-start gap-[16px]">
            {meta.length > 0 &&
              meta.map((item) => (
                <div key={item.text} className="flex items-center gap-[4px]">
                  <span className="text-[16px] text-[color:var(--neutral-500)]">
                    {item.icon}
                  </span>
                  <span className="text-[12px] font-normal text-[color:var(--neutral-500)]">
                    {item.text}
                  </span>
                </div>
              ))}
          </div>
        )}
        <div className="flex w-full flex-col items-start gap-[8px]">
          <h3 className="w-full text-[24px]  font-bold capitalize leading-[normal] text-[color:var(--neutral-900)]">
            {title}
          </h3>
          <p className="w-full  text-[16px] font-normal leading-[normal] text-[color:var(--neutral-500)]">
            {description}
          </p>
        </div>
        <div className="flex w-full flex-col flex-wrap items-center gap-[20px]">
          <Link
            to="/subscribe"
            search={courseSlug ? { course: courseSlug } : {}}
            className="w-full"
          >
            <Button className="w-full" size="default">
              Study Now
              <LuSend className="text-[24px]" />
            </Button>
          </Link>
          <Link
            to="/courses/$courseId"
            params={{ courseId: String(courseId) }}
            className="w-full"
            aria-label={`Explore ${title} course`}
          >
            <Button className="w-full" size="default" variant="outline">
              Explore Course
              <CgMoreO className="text-[24px]" />
            </Button>
          </Link>
          {price && (
            <div className="flex h-[52px] items-center gap-[8px] rounded-[999px] px-[16px] py-[4px]">
              <p className="text-[16px] font-normal line-through text-[color:var(--neutral-500)]">
                {priceOriginal}
              </p>
              <p className="text-[20px] font-semibold text-[color:var(--primary-600)] underline">
                {price}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
